import { Grid, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React from "react";
import { clampedTransactionPercentage } from "./helper/clampedTransactionPercentage";
import errorStatus from "./icons/errorStatus.svg";
import expiredStatus from "./icons/expiredStatus.svg";
import warningStatus from "./icons/warningStatus.svg";

const useStyle = makeStyles(theme => ({
  container: {
    display: "flex",
    alignItems: "center",
    paddingTop: theme.spacing(2)
  },
  barContainer: {
    position: "relative",
    display: "flex",
    flexGrow: 1,
    width: "100%",
    height: "5px",
    borderRadius: "5px",
    overflow: "hidden",
    backgroundColor: "#E3FAFF"
  },
  used: {
    backgroundColor: "#005F87",
    height: "5px",
    borderRadius: "5px 0 0 5px"
  },
  exceeded: {
    backgroundColor: "#DC6914",
    height: "5px",
    borderRadius: "5px"
  },
  allUsed: {
    backgroundColor: "#DC0000",
    height: "5px",
    borderRadius: "5px"
  },
  expiredBarContainer: {
    backgroundColor: "var(--Background-disabled-light, #00000026)"
  },
  expiredUsed: {
    backgroundColor: "var(--Controls-Control-disabled-shade-light, #00000080)",
    height: "5px",
    borderRadius: "5px"
  },
  percentageText: {
    marginLeft: theme.spacing(1),
    fontWeight: 400,
    fontSize: "10px",
    fontFamily: "Segoe UI",
    lineHeight: "14px",
    letterSpacing: 0,
    color: "#464646"
  },
  usedText: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "Segoe UI",
    fontWeight: 400,
    fontSize: "10px",
    lineHeight: "14px",
    letterSpacing: 0,
    color: "#6E6E6E"
  },
  margin: {
    marginLeft: 4
  },
  progressWrapper: {
    width: "100%",
    display: "flex",
    alignItems: "center"
  },
  barContainerMargin: {
    marginBottom: 0
  },
  warningIcon: {
    marginLeft: 8,
    height: 16
  },
  errorIcon: {
    marginLeft: 8,
    height: 16
  }
}));

interface DetailedTransationQuotaBarProps {
  usedTransactions: number;
  transactionLimit: number;
  status?: string;
}

const DetailedTransationQuotaBar: React.FC<DetailedTransationQuotaBarProps> = ({
  transactionLimit,
  usedTransactions,
  status
}) => {
  const classes = useStyle();

  const exceeded = usedTransactions > transactionLimit;
  const allUsed = usedTransactions === transactionLimit;
  const isExpired = status === "Expired" || status === "Deactivated";

  const percentage = Math.round((usedTransactions / transactionLimit) * 100);
  const width = `${clampedTransactionPercentage(
    usedTransactions,
    transactionLimit
  )}%`;

  // Determine bar state and configuration
  const getConfig = () => {
    if (isExpired && (exceeded || allUsed)) {
      return {
        barClass: classes.expiredUsed,
        containerClass: `${classes.barContainer} ${classes.expiredBarContainer}`,
        icon: {
          src: expiredStatus,
          alt: "Expired",
          className: classes.errorIcon
        }
      };
    }

    if (exceeded) {
      return {
        barClass: classes.exceeded,
        icon: {
          src: warningStatus,
          alt: "Warning",
          className: classes.warningIcon,
          testId: "quota-warning-icon"
        }
      };
    }

    if (allUsed) {
      return {
        barClass: classes.allUsed,
        icon: {
          src: errorStatus,
          alt: "All Used",
          className: classes.errorIcon,
          testId: "quota-error-icon"
        }
      };
    }

    if (isExpired) {
      return {
        barClass: classes.expiredUsed,
        containerClass: `${classes.barContainer} ${classes.expiredBarContainer}`,
        showPercentage: true
      };
    }

    return {
      barClass: classes.used,
      containerClass: `${classes.barContainer} ${classes.barContainerMargin}`,
      showPercentage: true
    };
  };

  const config = getConfig();

  // Helper to conditionally wrap the bar with container
  const ProgressBar = ({ children }: { children: React.ReactNode }) =>
    config.containerClass ? (
      <div className={config.containerClass}>{children}</div>
    ) : (
      <>{children}</>
    );

  return (
    <Grid container spacing={0} padding={0}>
      <Grid item xs={12} className={classes.container}>
        <div className={classes.progressWrapper}>
          <ProgressBar>
            <div className={config.barClass} style={{ width }} />
          </ProgressBar>

          {config.icon && (
            <img
              src={config.icon.src}
              alt={config.icon.alt}
              className={config.icon.className}
              {...(config.icon.testId && { "data-testid": config.icon.testId })}
            />
          )}

          {config.showPercentage && (
            <Typography className={classes.percentageText}>
              {percentage}%
            </Typography>
          )}
        </div>
      </Grid>
      <Grid item xs={12}>
        <Typography className={classes.usedText}>
          {usedTransactions <= transactionLimit ? (
            <>
              {usedTransactions}/{transactionLimit}
              <span className={classes.margin}>
                {allUsed ? "- You reached the limit" : "Used"}
              </span>
            </>
          ) : (
            <>
              {usedTransactions}/{transactionLimit}
              <span className={classes.margin}>
                - You have exceeded the limit
              </span>
            </>
          )}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default DetailedTransationQuotaBar;
